import proj4 from 'proj4';
import { PolygonClippedGridSystem, extentFromPolygon, } from '@zwaarcontrast/ol-graticule';
import { ProjectedGridSystem } from './grid-systems/ProjectedGridSystem.js';
import { registerCRS, syncOlProjections } from './registerCRS.js';
const DEFAULT_SOURCES = {
    definitionUrl: (code) => `https://epsg.io/${code}.proj4`,
    areaOfUseUrl: (code) => `https://spatialreference.org/ref/epsg/${code}/projjson.json`,
};
const EXTENT_MARGIN_M = 100_000;
const EDGE_SAMPLES = 8;
/** In-flight or settled lookups per code, shared by concurrent callers. */
const lookups = new Map();
function epsgNumber(code) {
    const n = typeof code === 'number' ? code : Number(code.replace(/^EPSG:/i, ''));
    if (!Number.isInteger(n) || n <= 0)
        throw new Error(`Not an EPSG code: ${String(code)}`);
    return n;
}
class HttpError extends Error {
    status;
    constructor(url, status) {
        super(`Unexpected response from ${url}: ${status}`);
        this.status = status;
    }
}
async function fetchText(url) {
    const response = await fetch(url);
    if (!response.ok)
        throw new HttpError(url, response.status);
    return response.text();
}
/** Grid files become optional, falling back to no shift until `loadNadgrid`. */
function optionalGrids(def) {
    return def.replace(/\+nadgrids=(\S+)/g, (_, list) => {
        const grids = list.split(',').map((g) => (g.startsWith('@') ? g : `@${g}`));
        if (!grids.includes('@null'))
            grids.push('@null');
        return `+nadgrids=${grids.join(',')}`;
    });
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function text(value) {
    return typeof value === 'string' ? value : undefined;
}
function areaOfUse(bbox) {
    if (!isRecord(bbox))
        return undefined;
    const { west_longitude, south_latitude, east_longitude, north_latitude } = bbox;
    if (typeof west_longitude !== 'number' ||
        typeof south_latitude !== 'number' ||
        typeof east_longitude !== 'number' ||
        typeof north_latitude !== 'number')
        return undefined;
    return {
        west: west_longitude,
        south: south_latitude,
        east: east_longitude < west_longitude ? east_longitude + 360 : east_longitude,
        north: north_latitude,
    };
}
function axes(coordinateSystem) {
    if (!isRecord(coordinateSystem))
        return [];
    const list = coordinateSystem['axis'];
    if (!Array.isArray(list))
        return [];
    return list.filter(isRecord).map((axis) => {
        const unit = axis['unit'];
        return {
            name: text(axis['name']) ?? '',
            abbreviation: text(axis['abbreviation']) ?? '',
            direction: text(axis['direction']) ?? '',
            unit: text(unit) ?? (isRecord(unit) ? text(unit['name']) : undefined) ?? '',
        };
    });
}
/** Top-level `scope`/`area`/`bbox`, or the first of `usages` when the CRS has several. */
function record(code, projjson) {
    const json = isRecord(projjson) ? projjson : {};
    const usages = json['usages'];
    const usage = Array.isArray(usages) && isRecord(usages[0]) ? usages[0] : json;
    return {
        code,
        crs: `EPSG:${code}`,
        name: text(json['name']),
        type: text(json['type']),
        area: text(usage['area']),
        scope: text(usage['scope']),
        bbox: areaOfUse(usage['bbox']),
        axes: axes(json['coordinate_system']),
    };
}
async function lookup(code, sources) {
    const crs = `EPSG:${code}`;
    const [def, projjson] = await Promise.allSettled([
        proj4.defs(crs) ? undefined : fetchText(sources.definitionUrl(code)),
        fetchText(sources.areaOfUseUrl(code)).then((body) => JSON.parse(body)),
    ]);
    if (def.status === 'rejected') {
        // epsg.io serves its 404 without CORS headers, so a browser sees only a
        // failed fetch; spatialreference.org's 404 says the code does not exist.
        const missing = projjson.status === 'rejected' &&
            projjson.reason instanceof HttpError &&
            projjson.reason.status === 404;
        throw missing ? new Error(`Unknown EPSG code: ${code}`) : def.reason;
    }
    if (def.value !== undefined)
        registerCRS(crs, optionalGrids(def.value.trim()));
    else
        syncOlProjections();
    return record(code, projjson.status === 'fulfilled' ? projjson.value : undefined);
}
/**
 * Everything spatialreference.org's PROJJSON gives for an EPSG code (name,
 * type, area of use in words and as a box, scope, axes and their units), with
 * the CRS registered with proj4 and OpenLayers from its epsg.io definition.
 *
 * Shares its per-code cache with {@link createProjectedGridSystemFromEPSG}, so
 * calling both for one code fetches it once.
 */
export async function lookupEPSG(code, sources) {
    const n = epsgNumber(code);
    let pending = lookups.get(n);
    if (!pending) {
        pending = lookup(n, { ...DEFAULT_SOURCES, ...sources });
        lookups.set(n, pending);
        pending.catch(() => lookups.delete(n));
    }
    return pending;
}
/** The area of use as a closed WGS84 ring, densified along each edge. */
function areaRing({ west, south, east, north }) {
    const ring = [];
    const edge = (from, to) => {
        for (let i = 0; i < EDGE_SAMPLES; i++) {
            const t = i / EDGE_SAMPLES;
            ring.push([
                from[0] + (to[0] - from[0]) * t,
                from[1] + (to[1] - from[1]) * t,
            ]);
        }
    };
    edge([west, south], [east, south]);
    edge([east, south], [east, north]);
    edge([east, north], [west, north]);
    edge([west, north], [west, south]);
    return ring;
}
/**
 * A projected grid for any EPSG code, fetched at runtime: the proj4
 * definition from epsg.io and the EPSG area of use from spatialreference.org.
 * The grid is clipped to that area; without one it is returned unclipped.
 *
 * Lookups are cached per code for the session, and a code already registered
 * with proj4 is not fetched again. `+nadgrids` references are made optional,
 * so load the grid with `loadNadgrid` for the full datum shift.
 *
 * ```ts
 * const grid = await createProjectedGridSystemFromEPSG(27700);
 * map.addLayer(new UniversalGraticule({ gridSystem: grid }));
 * ```
 */
export async function createProjectedGridSystemFromEPSG(code, options = {}) {
    const { sources, ...gridOptions } = options;
    const { crs, bbox } = await lookupEPSG(code, sources);
    if (!bbox)
        return new ProjectedGridSystem({ ...gridOptions, crs });
    const ring = areaRing(bbox);
    const toGrid = proj4('EPSG:4326', crs);
    const projected = ring.map(([lon, lat]) => {
        const [x, y] = toGrid.forward([lon, lat]);
        return [x ?? 0, y ?? 0];
    });
    return new PolygonClippedGridSystem({
        source: new ProjectedGridSystem({
            ...gridOptions,
            crs,
            extent: extentFromPolygon(projected, EXTENT_MARGIN_M),
        }),
        clipPolygon: { rings: [ring], crs: 'EPSG:4326' },
    });
}
//# sourceMappingURL=fromEPSG.js.map